import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiGetJson, apiPostJson, apiPutJson, apiDeleteJson } from "../../utils/apiClient";
import { CsvCol, downloadCsv } from "../../utils/csvExport";
import { isStandardEquivalentPlan } from "../../utils/plan";
import dayjs from "dayjs";

type SenderRow = {
  id: string;
  name: string;
  chatworkAccountId: number | null;
  chatworkAccountName: string;
  tokenLast4: string;
  createdAt: string | null;
  updatedAt: string | null;
  createdBy: string;
};

// ===== ルーム権限チェック =====
type RoomCheckStatus = "ok" | "readonly" | "not_member" | "no_room";

type RoomCheckRow = {
  tenantId: string;
  companyId: string;
  shopUrl: string;
  companyName: string;
  shopName: string;
  plan: string;
  roomId: string;
  status: RoomCheckStatus;
};

type RoomCheckState = {
  senderId: string;
  senderName: string;
  chatworkAccountName: string;
  loading: boolean;
  error: string;
  rows: RoomCheckRow[];
  problemsOnly: boolean;
  standardOnly: boolean;
};

const ROOM_CHECK_STATUS_LABEL: Record<RoomCheckStatus, string> = {
  ok: "投稿可",
  readonly: "閲覧のみ（要権限変更）",
  not_member: "未参加（要招待）",
  no_room: "連絡用ルームID未設定",
};

const ROOM_CHECK_STATUS_ORDER: Record<RoomCheckStatus, number> = {
  readonly: 0,
  not_member: 1,
  no_room: 2,
  ok: 3,
};

function roomCheckStatusClass(status: RoomCheckStatus) {
  if (status === "ok") return "text-emerald-700 font-semibold";
  if (status === "readonly") return "text-red-600 font-semibold";
  if (status === "not_member") return "text-amber-700 font-semibold";
  return "text-slate-500";
}

function fmtDate(iso: string | null) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function SuperChatworkSendersPage() {
  const [rows, setRows] = useState<SenderRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // 新規登録フォーム
  const [newName, setNewName] = useState("");
  const [newToken, setNewToken] = useState("");
  const [creating, setCreating] = useState(false);

  // 行ごとのトークン更新
  const [editingId, setEditingId] = useState("");
  const [editingToken, setEditingToken] = useState("");
  const [savingId, setSavingId] = useState("");

  // ルーム権限チェック
  const [check, setCheck] = useState<RoomCheckState | null>(null);

  const runRoomCheck = useCallback(async (sender: SenderRow) => {
    setError("");
    setInfo("");
    setCheck({
      senderId: sender.id,
      senderName: sender.name,
      chatworkAccountName: sender.chatworkAccountName,
      loading: true,
      error: "",
      rows: [],
      problemsOnly: true,
      standardOnly: true,
    });

    try {
      const [companies, roomsRes] = await Promise.all([
        apiGetJson<any[]>("/api/super/companies"),
        apiGetJson<{ roles: Record<string, string>; chatworkAccountName: string }>(
          `/api/super/chatwork-senders/${encodeURIComponent(sender.id)}/rooms`
        ),
      ]);

      const roles = roomsRes?.roles || {};
      const seen = new Set<string>();
      const rows: RoomCheckRow[] = [];

      for (const c of Array.isArray(companies) ? companies : []) {
        const tenantId = String(c?.tenantId ?? "").trim();
        const companyId = String(c?.companyId ?? "").trim();
        const shopUrl = String(c?.shopUrl ?? "").trim();
        if (!shopUrl) continue;
        const key = `${tenantId}__${companyId}__${shopUrl}`;
        if (seen.has(key)) continue;
        seen.add(key);

        const roomId = String(c?.chatworkContactRoomId ?? "").trim();
        let status: RoomCheckStatus;
        if (!roomId) status = "no_room";
        else if (!(roomId in roles)) status = "not_member";
        else if (roles[roomId] === "readonly") status = "readonly";
        else status = "ok";

        rows.push({
          tenantId,
          companyId,
          shopUrl,
          companyName: String(c?.companyName ?? "").trim(),
          shopName: String(c?.shopName ?? "").trim(),
          plan: String(c?.plan ?? "").trim(),
          roomId,
          status,
        });
      }

      rows.sort(
        (a, b) =>
          ROOM_CHECK_STATUS_ORDER[a.status] - ROOM_CHECK_STATUS_ORDER[b.status] ||
          a.shopUrl.localeCompare(b.shopUrl, "ja")
      );

      setCheck((p) =>
        p && p.senderId === sender.id ? { ...p, loading: false, rows } : p
      );
    } catch (e: any) {
      setCheck((p) =>
        p && p.senderId === sender.id
          ? { ...p, loading: false, error: e?.message || "チェックに失敗しました" }
          : p
      );
    }
  }, []);

  const checkVisibleRows = useMemo(() => {
    if (!check) return [];
    return check.rows.filter((r) => {
      if (check.standardOnly && !isStandardEquivalentPlan(r.plan)) return false;
      if (check.problemsOnly && r.status === "ok") return false;
      return true;
    });
  }, [check]);

  const checkSummary = useMemo(() => {
    const base = { ok: 0, readonly: 0, not_member: 0, no_room: 0 };
    if (!check) return base;
    for (const r of check.rows) {
      if (check.standardOnly && !isStandardEquivalentPlan(r.plan)) continue;
      base[r.status]++;
    }
    return base;
  }, [check]);

  const downloadCheckCsv = useCallback(() => {
    if (!check || !checkVisibleRows.length) return;
    const rows = checkVisibleRows.map((r) => ({
      shopUrl: r.shopUrl,
      companyName: r.companyName,
      shopName: r.shopName,
      plan: r.plan,
      roomId: r.roomId,
      status: ROOM_CHECK_STATUS_LABEL[r.status],
    }));
    const cols: CsvCol<(typeof rows)[0]>[] = [
      { key: "shopUrl", header: "ショップURL" },
      { key: "companyName", header: "会社名" },
      { key: "shopName", header: "ショップ名" },
      { key: "plan", header: "プラン" },
      { key: "roomId", header: "連絡用ルームID" },
      { key: "status", header: "判定" },
    ];
    downloadCsv(
      `chatwork_room_check_${check.senderName}_${dayjs().format("YYYYMMDDHHmmss")}`,
      rows,
      cols
    );
  }, [check, checkVisibleRows]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await apiGetJson<{ senders: SenderRow[] }>("/api/super/chatwork-senders");
      setRows(Array.isArray(r?.senders) ? r.senders : []);
    } catch (e: any) {
      setError(e?.message || "読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createSender = useCallback(async () => {
    if (creating) return;
    setError("");
    setInfo("");
    if (!newName.trim()) {
      setError("表示名を入力してください");
      return;
    }
    if (!newToken.trim()) {
      setError("APIトークンを入力してください");
      return;
    }
    setCreating(true);
    try {
      const r = await apiPostJson<{ ok: boolean; sender: SenderRow }>(
        "/api/super/chatwork-senders",
        { name: newName.trim(), token: newToken.trim() }
      );
      setRows((prev) => [...prev, r.sender]);
      setNewName("");
      setNewToken("");
      setInfo(
        `登録しました（Chatworkアカウント: ${r.sender.chatworkAccountName || "不明"}）`
      );
    } catch (e: any) {
      setError(e?.message || "登録に失敗しました");
    } finally {
      setCreating(false);
    }
  }, [creating, newName, newToken]);

  const updateToken = useCallback(
    async (id: string) => {
      if (savingId) return;
      setError("");
      setInfo("");
      if (!editingToken.trim()) {
        setError("新しいAPIトークンを入力してください");
        return;
      }
      setSavingId(id);
      try {
        const r = await apiPutJson<{ ok: boolean; sender: SenderRow }>(
          `/api/super/chatwork-senders/${encodeURIComponent(id)}`,
          { token: editingToken.trim() }
        );
        setRows((prev) => prev.map((x) => (x.id === id ? r.sender : x)));
        setEditingId("");
        setEditingToken("");
        setInfo(`トークンを更新しました（Chatworkアカウント: ${r.sender.chatworkAccountName || "不明"}）`);
      } catch (e: any) {
        setError(e?.message || "更新に失敗しました");
      } finally {
        setSavingId("");
      }
    },
    [savingId, editingToken]
  );

  const removeSender = useCallback(
    async (row: SenderRow) => {
      if (savingId) return;
      if (
        !window.confirm(
          `投稿者「${row.name}」を削除します。よろしいですか？\n（登録済みトークンも削除されます）`
        )
      ) {
        return;
      }
      setError("");
      setInfo("");
      setSavingId(row.id);
      try {
        await apiDeleteJson(`/api/super/chatwork-senders/${encodeURIComponent(row.id)}`);
        setRows((prev) => prev.filter((x) => x.id !== row.id));
        setInfo(`「${row.name}」を削除しました`);
      } catch (e: any) {
        setError(e?.message || "削除に失敗しました");
      } finally {
        setSavingId("");
      }
    },
    [savingId]
  );

  return (
    <div className="h-full flex flex-col gap-3 overflow-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-sm font-semibold text-slate-900">Chatwork投稿者管理</h1>
        <button
          className="border px-2 py-1 rounded text-xs bg-white hover:bg-gray-50 disabled:opacity-50"
          onClick={load}
          disabled={loading}
        >
          {loading ? "読込中..." : "再読み込み"}
        </button>
      </div>

      <div className="rounded border bg-blue-50 p-2 text-[11px] text-slate-700 leading-relaxed">
        Chatwork一斉投稿（会社一覧画面）で選べる「投稿者」を管理します。メッセージは選んだ投稿者の
        Chatworkアカウント名義で投稿されます。
        <ul className="list-disc ml-4 mt-1">
          <li>
            APIトークンは各担当者本人が Chatwork の「動作設定 → API → APIトークン」から発行し、ここに登録してください。
          </li>
          <li>投稿者本人が参加しているルームにしか投稿できません（未参加の顧客ルームは一斉投稿時に自動で除外されます）。</li>
          <li>トークンの値は Secret Manager にのみ保存され、この画面には下4桁だけ表示されます。</li>
        </ul>
      </div>

      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>
      )}
      {info && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">{info}</div>
      )}

      <div className="rounded border bg-white p-3">
        <div className="text-xs font-semibold mb-2">投稿者を追加</div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs">
            <div className="mb-1 text-slate-600">表示名（管理用）</div>
            <input
              type="text"
              className="border px-2 py-1 rounded text-sm w-48"
              placeholder="例）みっこ"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              disabled={creating}
            />
          </label>
          <label className="text-xs">
            <div className="mb-1 text-slate-600">Chatwork APIトークン</div>
            <input
              type="password"
              className="border px-2 py-1 rounded text-sm w-80"
              placeholder="本人が発行したAPIトークン"
              value={newToken}
              onChange={(e) => setNewToken(e.target.value)}
              disabled={creating}
            />
          </label>
          <button
            className="border px-3 py-1 rounded text-xs bg-slate-900 text-white disabled:opacity-50"
            onClick={createSender}
            disabled={creating}
          >
            {creating ? "検証中..." : "登録（トークン検証あり）"}
          </button>
        </div>
        <div className="mt-1 text-[11px] text-slate-500">
          登録時にトークンでChatwork APIへ接続し、有効性とアカウント名を確認します。
        </div>
      </div>

      <div className="rounded border bg-white overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-100">
            <tr>
              <th className="border px-2 py-1 text-left">表示名</th>
              <th className="border px-2 py-1 text-left">Chatworkアカウント</th>
              <th className="border px-2 py-1 text-left">トークン</th>
              <th className="border px-2 py-1 text-left">登録日時</th>
              <th className="border px-2 py-1 text-left">登録者</th>
              <th className="border px-2 py-1 text-left">操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="border px-2 py-3 text-slate-500">
                  {loading ? "読込中..." : "投稿者が未登録です。上のフォームから追加してください。"}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id}>
                  <td className="border px-2 py-1 font-semibold">{r.name}</td>
                  <td className="border px-2 py-1">
                    {r.chatworkAccountName || "-"}
                    {r.chatworkAccountId != null && (
                      <span className="text-slate-400 ml-1">(ID: {r.chatworkAccountId})</span>
                    )}
                  </td>
                  <td className="border px-2 py-1">••••{r.tokenLast4 || "----"}</td>
                  <td className="border px-2 py-1">{fmtDate(r.createdAt)}</td>
                  <td className="border px-2 py-1">{r.createdBy || "-"}</td>
                  <td className="border px-2 py-1">
                    {editingId === r.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="password"
                          className="border px-2 py-0.5 rounded text-xs w-56"
                          placeholder="新しいAPIトークン"
                          value={editingToken}
                          onChange={(e) => setEditingToken(e.target.value)}
                          disabled={savingId === r.id}
                        />
                        <button
                          className="border px-2 py-0.5 rounded text-xs bg-slate-900 text-white disabled:opacity-50"
                          onClick={() => updateToken(r.id)}
                          disabled={savingId === r.id}
                        >
                          {savingId === r.id ? "検証中..." : "保存"}
                        </button>
                        <button
                          className="border px-2 py-0.5 rounded text-xs bg-white hover:bg-gray-50"
                          onClick={() => {
                            setEditingId("");
                            setEditingToken("");
                          }}
                          disabled={savingId === r.id}
                        >
                          取消
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <button
                          className="border px-2 py-0.5 rounded text-xs bg-slate-900 text-white disabled:opacity-50"
                          onClick={() => runRoomCheck(r)}
                          disabled={!!savingId || (check?.loading ?? false)}
                          title="全顧客の連絡用ルームについて、この投稿者の参加状況・権限を一括チェックします"
                        >
                          {check?.loading && check.senderId === r.id ? "チェック中..." : "権限チェック"}
                        </button>
                        <button
                          className="border px-2 py-0.5 rounded text-xs bg-white hover:bg-gray-50"
                          onClick={() => {
                            setEditingId(r.id);
                            setEditingToken("");
                          }}
                          disabled={!!savingId}
                        >
                          トークン更新
                        </button>
                        <button
                          className="border px-2 py-0.5 rounded text-xs border-red-200 text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                          onClick={() => removeSender(r)}
                          disabled={!!savingId}
                        >
                          {savingId === r.id ? "削除中..." : "削除"}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {check && (
        <div className="rounded border bg-white p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs font-semibold">
              ルーム権限チェック: {check.senderName}
              {check.chatworkAccountName && (
                <span className="text-slate-500 font-normal ml-1">
                  （Chatwork: {check.chatworkAccountName}）
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs">
              <label className="flex items-center gap-1 select-none">
                <input
                  type="checkbox"
                  checked={check.standardOnly}
                  onChange={(e) =>
                    setCheck((p) => (p ? { ...p, standardOnly: e.target.checked } : p))
                  }
                />
                スタンダード相当プランのみ
              </label>
              <label className="flex items-center gap-1 select-none">
                <input
                  type="checkbox"
                  checked={check.problemsOnly}
                  onChange={(e) =>
                    setCheck((p) => (p ? { ...p, problemsOnly: e.target.checked } : p))
                  }
                />
                問題のあるルームのみ表示
              </label>
              <button
                className="border px-2 py-1 rounded text-xs bg-slate-900 text-white disabled:opacity-50"
                onClick={downloadCheckCsv}
                disabled={check.loading || checkVisibleRows.length === 0}
              >
                CSV出力（表示中の{checkVisibleRows.length}件）
              </button>
              <button
                className="border px-2 py-1 rounded text-xs bg-white hover:bg-gray-50"
                onClick={() => setCheck(null)}
                disabled={check.loading}
              >
                閉じる
              </button>
            </div>
          </div>

          {check.error && (
            <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
              {check.error}
            </div>
          )}

          {check.loading ? (
            <div className="mt-3 text-xs text-slate-500">チェック中...（全ルームの参加状況を取得しています）</div>
          ) : (
            <>
              <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
                <div className="rounded border bg-emerald-50 p-2">
                  投稿可: <b>{checkSummary.ok}</b> 件
                </div>
                <div className="rounded border bg-red-50 p-2">
                  閲覧のみ（要権限変更）: <b>{checkSummary.readonly}</b> 件
                </div>
                <div className="rounded border bg-amber-50 p-2">
                  未参加（要招待）: <b>{checkSummary.not_member}</b> 件
                </div>
                <div className="rounded border bg-slate-50 p-2">
                  ルームID未設定: <b>{checkSummary.no_room}</b> 件
                </div>
              </div>

              <div className="mt-3 max-h-[50vh] overflow-auto border rounded">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-100">
                    <tr>
                      <th className="border px-2 py-1 text-left">ショップURL</th>
                      <th className="border px-2 py-1 text-left">会社名</th>
                      <th className="border px-2 py-1 text-left">ショップ名</th>
                      <th className="border px-2 py-1 text-left">プラン</th>
                      <th className="border px-2 py-1 text-left">連絡用ルームID</th>
                      <th className="border px-2 py-1 text-left">判定</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checkVisibleRows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="border px-2 py-3 text-slate-500">
                          {check.problemsOnly
                            ? "問題のあるルームはありません 🎉（フィルタを外すと全件表示できます）"
                            : "対象がありません"}
                        </td>
                      </tr>
                    ) : (
                      checkVisibleRows.map((r) => (
                        <tr key={`${r.tenantId}__${r.companyId}__${r.shopUrl}`}>
                          <td className="border px-2 py-1">{r.shopUrl}</td>
                          <td className="border px-2 py-1">{r.companyName || "-"}</td>
                          <td className="border px-2 py-1">{r.shopName || "-"}</td>
                          <td className="border px-2 py-1">{r.plan || "-"}</td>
                          <td className="border px-2 py-1">{r.roomId || "-"}</td>
                          <td className={`border px-2 py-1 ${roomCheckStatusClass(r.status)}`}>
                            {ROOM_CHECK_STATUS_LABEL[r.status]}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-2 text-[11px] text-slate-500">
                ※「閲覧のみ」はルーム管理者がメンバー権限を変更、「未参加」はルームへの招待で解消できます。
                変更後にもう一度「権限チェック」を押すと最新状態で再判定します。
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
